import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import { getSalesByIdSlice } from "../../../store/slices/sales";
import s from "./RecieptreportPdf.module.scss";
import qrcodeImg from "../../../images/qr.png";

const ReceiptreportPdf = () => {
  const dispatch = useDispatch();
  const componentRef = useRef();

  // --- REDUX DATA ---
  const { clinicInfo } = useSelector((state) => state.clinic);
  const clinic = clinicInfo && clinicInfo?.length > 0 ? clinicInfo[0] : null;
  const { salesById } = useSelector((state) => state.sales);
  const { token } = useSelector((state) => state.auth);
  const receiptId = sessionStorage.getItem("receiptId");

  // --- FETCH DATA ---
  useEffect(() => {
    if (receiptId && token) {
      dispatch(getSalesByIdSlice({ id: receiptId, token }));
    }
  }, [dispatch, receiptId, token]);

  const [pdfHeader, setPdfHeader] = useState(null);
  const [isoLogo, setIsoLogo] = useState(null);
  const [watermarkImg, setWatermarkImg] = useState(null);

  // --- DYNAMIC CLINIC ADDRESS ---
  const dynamicClinicAddress = `
    ${clinic?.clinicAddress?.plot ? `${clinic.clinicAddress.plot}, ` : ""}
    ${clinic?.clinicAddress?.area ? `${clinic.clinicAddress.area}, ` : ""}
    ${clinic?.clinicAddress?.street ? `${clinic.clinicAddress.street}, ` : ""}
    ${clinic?.clinicAddress?.landMark ? `${clinic.clinicAddress.landMark}, ` : ""}
    ${clinic?.clinicAddress?.city?.name ? `${clinic.clinicAddress.city.name}, ` : ""}
    ${clinic?.clinicAddress?.state?.name ? `${clinic.clinicAddress.state.name}, ` : ""}
    ${clinic?.clinicAddress?.country?.name ? `${clinic.clinicAddress.country.name} ` : ""}
    ${clinic?.clinicAddress?.pin ? clinic.clinicAddress.pin : ""}
  `.trim();

  const toBase64 = async (url) => {
    if (!url) return null;
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const loadImages = async () => {
      if (clinic?.hospitalInfo?.pdfHeader)
        setPdfHeader(await toBase64(clinic.hospitalInfo.pdfHeader));
      if (clinic?.hospitalInfo?.ISOLogo)
        setIsoLogo(await toBase64(clinic.hospitalInfo.ISOLogo));
      if (clinic?.hospitalInfo?.waterMark)
        setWatermarkImg(await toBase64(clinic.hospitalInfo.waterMark));
    };
    loadImages();
  }, [clinic]);

  // --- DATA MAPPING ---
  const saleData = salesById?.sale;
  const appointmentDetails = saleData?.invoiceId?.[0]?.appointmentDetails || {};
  const patientData = saleData?.appointmentId?.[0]?.patientId?.userId || {};
  const patientUHID = saleData?.appointmentId?.[0]?.patientId?.UHID || "N/A";
  const physicianName = saleData?.appointmentId?.[0]?.physicianId?.userId?.name;
  let rows = [];
  const addRow = (name, rate, tax = 0, isConsultation = false) => {
    if (!name) return;
    const r = Number(rate || 0);
    const t = isConsultation ? 0 : Number(tax || 0);
    const taxAmount = (r * t) / 100;
    const total = r + taxAmount;
    rows.push({
      name,
      rate: r,
      tax: t,
      sgst: t / 2,
      cgst: t / 2,
      total,
      isConsultation,
    });
  };

  if (saleData) {
    const isClient = saleData?.invoiceId?.[0]?.isClient === "Yes";

    (appointmentDetails.tests || []).forEach((it) =>
      addRow(it?.testId?.testName, it?.price, it?.testId?.tax),
    );
    (appointmentDetails.profiles || []).forEach((it) =>
      addRow(it?.profileId?.profileName, it?.price, it?.profileId?.tax),
    );
    (appointmentDetails.consultationDetails || []).forEach((it) =>
      addRow(`Dr. ${physicianName || "N/A"}`, it?.price, 0, true),
    );

    (appointmentDetails.radioServices || []).forEach((it) => {
      it?.radiologyServiceInfo?.serviceDetails?.forEach((det) =>
        addRow(
          det?.radioId?.serviceDetailsName || det?.serviceName,
          det?.price,
          det?.radioId?.tax || it?.tax,
        ),
      );
    });

    (appointmentDetails.skinServices || []).forEach((it) =>
      addRow(
        it?.skinId?.serviceName,
        isClient ? it?.client_price : it?.pt_price,
        it?.skinId?.tax,
      ),
    );
    (appointmentDetails.dentalServices || []).forEach((it) =>
      addRow(it?.dentalId?.serviceName, it?.price, it?.dentalId?.tax),
    );
    (appointmentDetails.hairServices || []).forEach((it) =>
      addRow(it?.hairId?.serviceName, it?.price, it?.hairId?.tax),
    );
    (appointmentDetails.generalServices || []).forEach((it) =>
      addRow(it?.generalId?.serviceName, it?.price, it?.generalId?.tax),
    );
    (appointmentDetails.physiotherapyService || []).forEach((it) =>
      addRow(it?.physioId?.serviceDetailName, it?.price, it?.physioId?.tax),
    );
    (appointmentDetails.healthScreeningDetails || []).forEach((it) =>
      addRow(it?.healthScreeningName, it?.healthScreeningAmount, it?.tax),
    );
    (appointmentDetails.healthPackageDetails || []).forEach((it) =>
      addRow(it?.healthPackageName, it?.healthPackageAmount, it?.tax),
    );
  }

  const grossTotal = Number(saleData?.totalPrice || 0);
  const discount = Number(saleData?.discountPrice || 0);
  const netPayable = grossTotal - discount;
  const paidAmount = Array.isArray(saleData?.paymentDetails)
    ? saleData.paymentDetails.reduce((sum, p) => sum + (p.paidAmount || 0), 0)
    : 0;
  const balanceDue = Number(saleData?.TotalDueAmount || 0);

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Receipt_${saleData?.receiptId || "bill"}`,
  });

  if (!salesById || !saleData)
    return <div className="p-5 text-center">Loading Data...</div>;

  return (
    <div className={s.mainWrapper}>
      <div className={`${s.actionBar} no-print`}>
        <div className={s.pageCount}>
          Receipt ID: <strong>{saleData?.receiptId}</strong>
        </div>
        <button className={s.btnDownload} onClick={handlePrint}>
          Download Receipt
        </button>
      </div>

      <div ref={componentRef} className={s.reportPage}>
        {watermarkImg && (
          <img src={watermarkImg} alt="Watermark" className={s.watermark} />
        )}

        <div className={s.contentWrapper} style={{ flex: 1 }}>
          <div className={s.headerSection}>
            <div className={s.headerLeft}>
              {pdfHeader && (
                <img src={pdfHeader} alt="Logo" className={s.labLogo} />
              )}
              <div className={s.addressText}>
                <strong>{clinic?.clinicName || "SimpleeKare Health"}</strong>{" "}
                <br />
                {dynamicClinicAddress} <br />
                Contact: {clinic?.phone} | GSTIN:{" "}
                {clinic?.accountInfo?.gst || "N/A"}
              </div>
            </div>
            <div className={s.headerRight}>
              <div className={s.trustedWrapper}>
                {isoLogo && (
                  <img src={isoLogo} alt="Logo" className={s.trustedLogo} />
                )}
                <div className={s.trustedText}>
                  <p className={s.trustedTitle}>Most Trusted Brand</p>
                  <div className={s.statBlock}>
                    <span className={s.trustedBold}>40 lakhs+</span>
                    <span className={s.subText}>satisfied customers</span>
                  </div>
                  <div className={s.statBlock}>
                    <span className={s.trustedBold}>1 crore+</span>
                    <span className={s.subText}>lab tests booked</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={s.blackline}></div>

          <div className={s.barcodeRow}>
            <Barcode
              value={saleData?.receiptId || "N/A"}
              height={25}
              width={1.2}
              displayValue={false}
            />
          </div>

          <div className={s.patientDetailsGrid}>
            <table className={s.patientTable}>
              <tbody>
                <tr>
                  <td className={s.label}>From:</td>
                  <td>: {patientData?.address?.city?.name || "N/A"}</td>
                  <td className={s.label}>Receipt ID</td>
                  <td>: {saleData?.receiptId}</td>
                </tr>
                <tr>
                  <td className={s.label}>Patient Name</td>
                  <td>: {patientData?.name}</td>
                  <td className={s.label}>Date</td>
                  <td>
                    :{" "}
                    {saleData?.createdAt
                      ? new Date(saleData.createdAt).toLocaleDateString("en-GB")
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <td className={s.label}>Phone / Email</td>
                  <td>
                    : {patientData?.phone} / {patientData?.email || "N/A"}
                  </td>
                  <td className={s.label}>Billed By</td>
                  <td>: {clinic?.clinicName || "SimpleeKare"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className={s.testCategoryHeader}>TAX RECEIPT SUMMARY</div>
          <table className={s.resultsTable}>
            <thead>
              <tr style={{ borderBottom: "2px solid #000" }}>
                <th style={{ width: "8%" }}>Sl No</th>
                <th style={{ width: "42%" }}>Item Name</th>
                <th style={{ width: "10%", textAlign: "center" }}>Service</th>
                <th style={{ width: "10%", textAlign: "center" }}>Rate</th>
                <th style={{ width: "10%", textAlign: "center" }}>Tax %</th>
                <th style={{ width: "10%", textAlign: "center" }}>SGST</th>
                <th style={{ width: "10%", textAlign: "center" }}>CGST</th>
                <th style={{ width: "10%", textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>{appointmentDetails?.service?.serviceName}</td>
                  <td style={{ textAlign: "center" }}>{row.rate.toFixed(2)}</td>
                  <td style={{ textAlign: "center" }}>
                    {row.isConsultation ? "-" : `${row.tax.toFixed(2)}%`}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {row.isConsultation ? "-" : `${row.sgst.toFixed(2)}%`}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    {row.isConsultation ? "-" : `${row.cgst.toFixed(2)}%`}
                  </td>
                  <td style={{ textAlign: "right" }}>{row.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={s.totalsSection}>
            <div className={s.totalsBox}>
              <div className={s.totalRow}>
                <span>Gross Amount:</span>{" "}
                <strong>{grossTotal.toFixed(2)}</strong>
              </div>
              <div className={s.totalRow}>
                <span>Discount:</span>{" "}
                <strong style={{ color: "red" }}>
                  - {discount.toFixed(2)}
                </strong>
              </div>
              <div className={s.netRow}>
                <span>Net Payable:</span>{" "}
                <strong>{netPayable.toFixed(2)}</strong>
              </div>
              <div className={s.totalRow}>
                <span>Paid Amount:</span>{" "}
                <strong style={{ color: "#1bc38b" }}>
                  {paidAmount.toFixed(2)}
                </strong>
              </div>
              <div className={s.balanceRow}>
                <span>Balance Due:</span>
                <span style={{ color: balanceDue > 0 ? "red" : "#1bc38b" }}>
                  {balanceDue.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* --- FOOTER SECTION (Pinned to bottom via position: absolute) --- */}
        <div className={s.reportFooter}>
          <div className={s.footerLeft}>
            <img src={qrcodeImg} alt="QR" className={s.footerQr} />
          </div>
          <div className={s.footerCenter}>
            <div className={s.billedByContainer}>
              <p className={s.billedByText}>
                Billed By : {clinic?.clinicName || "SimpleeKare Health"}
              </p>
              <p className={s.patientIdText}>Patient ID: {patientUHID}</p>
            </div>
          </div>
          <div className={s.footerRight}>
            <div className={s.signatureArea}>
              {clinic?.hospitalInfo?.authorizedSignature && (
                <img
                  src={clinic.hospitalInfo.authorizedSignature}
                  alt="Sign"
                  className={s.signImg}
                />
              )}
              <p className={s.signName}>Authorised Signatory</p>
              <p className={s.signTitle}>
                {clinic?.clinicName || "SimpleeKare Health"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptreportPdf;
